import { useCallback, useEffect, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { ColumnState } from 'ag-grid-community';
import { supabaseCoreClient } from '../utils/supabase.js';
import { queryPolicies } from '../utils/queryPolicies.js';
import { appUserIdFromSession } from '../utils/appUserFromSession.js';
import type { GridDefaultsSnapshot } from '../utils/gridColumnDefaults.js';

/**
 * REG-828: each user's column layout for a grid (which columns show, their
 * order, widths, pinning, sort), saved per user rather than per browser so it
 * follows them to any device. Stored in core_crm.user_dashboard_state, the
 * existing per-user UI state table (row-level security to the user's own
 * rows), under the key `grid:<gridKey>`; dashboards use their own keys there.
 * A grid with admin default columns also records which default the layout was
 * saved against (`defaults`, see gridColumnDefaults.ts).
 */
export const GRID_LAYOUT_KEY_PREFIX = 'grid:';
const SAVE_DEBOUNCE_MS = 500;

export function gridLayoutStateKey(gridKey: string): string {
  return `${GRID_LAYOUT_KEY_PREFIX}${gridKey}`;
}

export { filterColumnState } from '../utils/columnState.js';

function readLegacyLayout(storageKey: string): ColumnState[] | null {
  try {
    const raw = localStorage.getItem(storageKey);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) && parsed.length > 0 ? parsed : null;
  } catch {
    return null;
  }
}

function forgetLegacyLayout(storageKey: string): void {
  try {
    localStorage.removeItem(storageKey);
  } catch {
    /* storage unavailable */
  }
}

export interface SavedGridLayout {
  columnState: ColumnState[];
  defaults: GridDefaultsSnapshot | null;
}

function asDefaultsSnapshot(value: unknown): GridDefaultsSnapshot | null {
  const candidate = value as GridDefaultsSnapshot | null | undefined;
  if (!candidate || typeof candidate.version !== 'number' || !Array.isArray(candidate.visible)) return null;
  return { version: candidate.version, visible: candidate.visible.filter((id) => typeof id === 'string') };
}

export async function loadGridLayout(gridKey: string): Promise<SavedGridLayout | null> {
  const { data, error } = await (supabaseCoreClient as any)
    .from('user_dashboard_state')
    .select('filter_values')
    .eq('dashboard_key', gridLayoutStateKey(gridKey))
    .maybeSingle();
  if (error) throw error;
  const columnState = data?.filter_values?.columnState;
  if (!Array.isArray(columnState)) return null;
  return { columnState, defaults: asDefaultsSnapshot(data?.filter_values?.defaults) };
}

export async function saveGridLayout(
  gridKey: string,
  columnState: ColumnState[],
  defaults: GridDefaultsSnapshot | null = null,
): Promise<void> {
  const { data: { session } } = await supabaseCoreClient.auth.getSession();
  const appUserId = appUserIdFromSession(session);
  if (!appUserId) return; // signed out: nothing to save the layout against
  const { error } = await (supabaseCoreClient as any)
    .from('user_dashboard_state')
    .upsert(
      {
        app_user: appUserId,
        dashboard_key: gridLayoutStateKey(gridKey),
        filter_values: defaults ? { columnState, defaults } : { columnState },
      },
      { onConflict: 'app_user,dashboard_key' },
    );
  if (error) throw error;
}

/**
 * Server writes of one grid's layout, in the order they were made. A save is
 * debounced and a reset deletes right away, so without this a reset clicked
 * while the save is still in flight can reach the database first: the delete
 * finds nothing, the save then lands, and the layout the user just reset comes
 * back on their next visit. Chaining per grid key makes the last action win.
 * Do not run these writes in parallel unless they carry an ordering guarantee
 * of their own (for example a version the server compares).
 */
const layoutWriteChains = new Map<string, Promise<void>>();

function enqueueLayoutWrite(gridKey: string, write: () => Promise<void>): Promise<void> {
  const previous = layoutWriteChains.get(gridKey) ?? Promise.resolve();
  const next = previous.catch(() => undefined).then(write);
  layoutWriteChains.set(gridKey, next);
  const forget = () => {
    if (layoutWriteChains.get(gridKey) === next) layoutWriteChains.delete(gridKey);
  };
  next.then(forget, forget);
  return next;
}

export async function deleteGridLayout(gridKey: string): Promise<void> {
  const { error } = await (supabaseCoreClient as any)
    .from('user_dashboard_state')
    .delete()
    .eq('dashboard_key', gridLayoutStateKey(gridKey));
  if (error) throw error;
}

/**
 * The layout a grid should restore on mount, plus save / clear.
 *
 * `initialColumnState` is fixed once it has loaded for a key, so a grid applies
 * it exactly once; later saves update the cache for the next mount only.
 * `legacyStorageKey` moves a layout the grid used to keep in localStorage to the
 * server the first time this user opens it, then forgets the local copy.
 */
export function useGridLayout(gridKey: string | undefined, options: { legacyStorageKey?: string } = {}) {
  const { legacyStorageKey } = options;
  const queryClient = useQueryClient();
  const queryKey = ['grid-layout', gridKey ?? ''];
  const query = useQuery({
    queryKey,
    queryFn: async (): Promise<SavedGridLayout | null> => {
      const saved = await loadGridLayout(gridKey!);
      if (saved || !legacyStorageKey) return saved;
      const legacy = readLegacyLayout(legacyStorageKey);
      if (!legacy) return null;
      await saveGridLayout(gridKey!, legacy);
      forgetLegacyLayout(legacyStorageKey);
      return { columnState: legacy, defaults: null };
    },
    enabled: Boolean(gridKey),
    retry: 1,
    ...queryPolicies.configuration,
  });

  const [initial, setInitial] = useState<{ key: string; layout: SavedGridLayout | null } | null>(null);
  useEffect(() => {
    if (!gridKey || query.isPending || initial?.key === gridKey) return;
    if (query.error) console.error(`Failed to load the saved columns for ${gridKey}:`, query.error);
    setInitial({ key: gridKey, layout: query.data ?? null });
  }, [gridKey, initial?.key, query.data, query.error, query.isPending]);

  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pendingRef = useRef<{ key: string; state: ColumnState[]; defaults: GridDefaultsSnapshot | null } | null>(null);

  const flush = useCallback(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = null;
    const pending = pendingRef.current;
    pendingRef.current = null;
    if (!pending) return;
    enqueueLayoutWrite(pending.key, () => saveGridLayout(pending.key, pending.state, pending.defaults)).catch((error) =>
      console.error(`Failed to save the columns for ${pending.key}:`, error),
    );
  }, []);

  // A layout changed just before leaving the page is still saved.
  useEffect(() => flush, [flush, gridKey]);

  const save = useCallback((columnState: ColumnState[], defaults: GridDefaultsSnapshot | null = null) => {
    if (!gridKey) return;
    queryClient.setQueryData<SavedGridLayout>(['grid-layout', gridKey], { columnState, defaults });
    pendingRef.current = { key: gridKey, state: columnState, defaults };
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(flush, SAVE_DEBOUNCE_MS);
  }, [flush, gridKey, queryClient]);

  const clear = useCallback(() => {
    if (!gridKey) return;
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = null;
    pendingRef.current = null;
    queryClient.setQueryData(['grid-layout', gridKey], null);
    if (legacyStorageKey) forgetLegacyLayout(legacyStorageKey);
    enqueueLayoutWrite(gridKey, () => deleteGridLayout(gridKey)).catch((error) =>
      console.error(`Failed to reset the saved columns for ${gridKey}:`, error),
    );
  }, [gridKey, legacyStorageKey, queryClient]);

  const loaded = !gridKey || initial?.key === gridKey;
  return {
    loaded,
    initialColumnState: loaded && gridKey ? initial?.layout?.columnState ?? null : null,
    /** The admin default the restored layout was saved against, if recorded. */
    initialDefaults: loaded && gridKey ? initial?.layout?.defaults ?? null : null,
    save,
    clear,
  };
}
